import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { TranslateService } from '@ngx-translate/core';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { CampaignType } from '@modules/campaigns/models/campaign.model';

export interface CampaignDropdownOption {
  label: string;
  value: string;
  status: string;
  type?: CampaignType;
}

interface OptionGroup {
  label: string;
  items: CampaignDropdownOption[];
}

const STATUS_ORDER: Record<string, number> = { published: 0, paused: 1, unpublished: 2, draft: 3 };
const TYPE_ORDER: Record<string, number> = { drip: 0, pill: 1 };

@Component({
  selector: 'pay-campaign-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule, LucideIconsModule],
  templateUrl: './campaign-dropdown.component.html',
  styleUrls: ['./campaign-dropdown.component.scss'],
})
export class CampaignDropdownComponent {
  private _options: CampaignDropdownOption[] = [];

  @Input() set options(v: CampaignDropdownOption[]) {
    this._options = v;
    this._groupedOptions = this.buildGroups(v);
  }
  @Input() placeholder = '';
  @Input() disabled = false;

  @Input() set selectedId(v: string) { this.internalValue = v ?? ''; }
  @Output() selectedIdChange = new EventEmitter<string>();

  internalValue = '';
  _groupedOptions: OptionGroup[] = [];

  constructor(private readonly translate: TranslateService) {}

  get searchPlaceholder(): string {
    return this.translate.instant('DATA.COMMON.ACTIONS.SEARCH');
  }

  onSelectionChange(value: string | null): void {
    const v = value ?? '';
    this.internalValue = v;
    this.selectedIdChange.emit(v);
  }

  getCampaignTypeIcon(type?: CampaignType): string {
    return type === 'drip' ? 'arrow-right' : 'zap';
  }

  getCampaignStatusLabel(status: string): string {
    const keyMap: Record<string, string> = {
      published:   'DATA.MODULES.CAMPAINGS.EDIT.PUBLISHED',
      paused:      'DATA.MODULES.CAMPAINGS.EDIT.PAUSED',
      unpublished: 'DATA.MODULES.CAMPAINGS.EDIT.UNPUBLISHED',
    };
    const key = keyMap[status];
    return key ? this.translate.instant(key) : status;
  }

  getCampaignTypeLabel(type?: CampaignType): string {
    if (!type) return '';
    const key = type === 'pill'
      ? 'DATA.MODULES.CAMPAINGS.GENERIC_INFO.TYPE.PILL_SIMPLE'
      : 'DATA.MODULES.CAMPAINGS.GENERIC_INFO.TYPE.DRIP_SIMPLE';
    return this.translate.instant(key);
  }

  private buildGroups(opts: CampaignDropdownOption[]): OptionGroup[] {
    const groupMap = new Map<string, { items: CampaignDropdownOption[]; status: string; type: string }>();

    for (const opt of opts) {
      const key = `${opt.status}|${opt.type ?? ''}`;
      if (!groupMap.has(key)) groupMap.set(key, { items: [], status: opt.status, type: opt.type ?? '' });
      groupMap.get(key)!.items.push(opt);
    }

    return [...groupMap.entries()]
      .sort(([, a], [, b]) => {
        const wa = (STATUS_ORDER[a.status] ?? 99) * 10 + (TYPE_ORDER[a.type] ?? 99);
        const wb = (STATUS_ORDER[b.status] ?? 99) * 10 + (TYPE_ORDER[b.type] ?? 99);
        return wa - wb;
      })
      .map(([, { items, status, type }]) => {
        const statusLabel = this.getCampaignStatusLabel(status);
        const typeLabel = type ? this.getCampaignTypeLabel(type as CampaignType) : '';
        return { label: typeLabel ? `${statusLabel} · ${typeLabel}` : statusLabel, items };
      });
  }
}
